import React, { useCallback, useEffect, useRef, useState } from "react";
import config from "../config.js";
import { detailToString } from '../utils/apiError';
import { fmtMoney, asOf } from '../utils/priceMath';
import { preferredPriceFor, stepMatchesBook, describePriceBookApply } from '../utils/partEntryCostMath';

/**
 * Per-step outsourcing on a part's routing.
 *
 * Until this existed, "this step goes to an outside vendor" could not be SAID. The flag on
 * part_processes had no writer that could set it for anything but Coating -- every writer
 * hardcodes the process name -- so the concept existed in the database and nowhere a person
 * could reach it.
 *
 * Three deliberate choices here:
 *
 *   - The checkbox is DISABLED WITH THE SERVER'S OWN REASON, never hidden. The API returns
 *     `can_outsource` and `outsource_blocked_reason` from the same function that enforces the
 *     rule, so the tooltip and the 400 can never disagree. Hiding the box would leave the
 *     operator wondering why Brake cannot be sent out; showing it greyed with "the Brake
 *     station still carries a shop rate of $26.00/hr, so the vendor price would be added on
 *     top of internal time" answers the question where it is asked.
 *
 *   - The vendor price book for this part is shown right beside the decision. A part can
 *     hold several vendors' prices at once, which is the whole reason the table exists:
 *     OPO 0017 shipped Bicknel's $11.50 to Alexander, who charges $3.25, because the part
 *     could only hold one number.
 *
 *   - Wave 2 (decision 3): this card is the SINGLE editor of an outsourced $/piece. The Part
 *     page's legacy "Coating $/unit" box mirrors the Coating row read-only and its Save no
 *     longer sends the column. Each price-book row gets an "Apply" affordance: POST
 *     …/process_prices/{vpp}/set-preferred, which now writes the book price through to the
 *     routing row (and parts.coating_* for Coating), recomputes the part rollup and returns
 *     `applied: [{part_id, process, old, new}]` — echoed here as the notice. `onData` hands
 *     the loaded rows to the parent so the mirror can name the vendor and the date.
 */
export default function PartOutsourcingCard({ partId, onChanged, onData }) {
  const API_BASE = config.API_BASE_URL;

  const [steps, setSteps] = useState([]);
  const [prices, setPrices] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  // The parent's callback lives in a ref so `load` (and the mount effect that calls it)
  // never depends on the parent's render identity — a fresh arrow each render would
  // otherwise refetch on every parent re-render.
  const onDataRef = useRef(onData);
  useEffect(() => { onDataRef.current = onData; }, [onData]);

  // Every load() takes a ticket. A response whose ticket is no longer current — a
  // newer load started (Refresh, a PATCH, Apply) or this instance unmounted (the Part
  // page remounts the card per part select) — is dropped, not applied. Otherwise part
  // A's rows could land after part B's and reach the parent's mirror as if they were B's.
  const loadSeqRef = useRef(0);
  useEffect(() => () => { loadSeqRef.current += 1; }, []);

  const load = useCallback(async () => {
    if (!partId) return;
    const seq = ++loadSeqRef.current;
    setLoading(true);
    setError("");
    try {
      const [s, p, v] = await Promise.all([
        fetch(`${API_BASE}/parts/${partId}/processes`),
        fetch(`${API_BASE}/parts/${partId}/process_prices`),
        fetch(`${API_BASE}/vendors`),
      ]);
      if (!s.ok) throw new Error(`routing: ${s.status}`);
      const stepRows = await s.json();
      const priceRows = p.ok ? await p.json() : [];
      const allVendors = v.ok ? await v.json() : [];
      if (seq !== loadSeqRef.current) return;
      const stepList = Array.isArray(stepRows) ? stepRows : [];
      const priceList = Array.isArray(priceRows) ? priceRows : [];
      setSteps(stepList);
      setPrices(priceList);
      setVendors((Array.isArray(allVendors) ? allVendors : []).filter((x) => x.is_service));
      if (onDataRef.current) onDataRef.current({ steps: stepList, prices: priceList });
    } catch (e) {
      if (seq !== loadSeqRef.current) return;
      setError(e.message || "Could not load the part's routing.");
    } finally {
      if (seq === loadSeqRef.current) setLoading(false);
    }
  }, [API_BASE, partId]);

  useEffect(() => { load(); }, [load]);

  const patchStep = async (step, body) => {
    setBusyId(step.part_process_id);
    setError("");
    setNotice("");
    try {
      const r = await fetch(
        `${API_BASE}/parts/${partId}/processes/${step.part_process_id}`,
        { method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) }
      );
      const data = await r.json().catch(() => ({}));
      if (!r.ok) {
        // The server's refusal is the message worth showing -- it explains WHY.
        setError(detailToString(data.detail) || `Update failed (${r.status}).`);
        return;
      }
      await load();
      if (onChanged) onChanged();
    } catch (e) {
      setError(e.message || "Update failed.");
    } finally {
      setBusyId(null);
    }
  };

  // Make `price` the preferred vendor for its process; the API writes its $/piece
  // through to the routing row (and parts.coating_* for Coating) and says what moved.
  const applyBookPrice = async (step, price) => {
    setBusyId(step.part_process_id);
    setError("");
    setNotice("");
    try {
      const r = await fetch(
        `${API_BASE}/parts/${partId}/process_prices/${price.vendor_process_price_id}/set-preferred`,
        { method: "POST" }
      );
      const data = await r.json().catch(() => ({}));
      if (!r.ok) {
        setError(detailToString(data.detail) || `Apply failed (${r.status}).`);
        return;
      }
      const msg = describePriceBookApply(data, {
        stepName: step.process_name, vendorName: price.vendor_name, price: price.price_per_piece,
      });
      setNotice(msg.text);
      await load();
      // The preferred flag moved even when nothing was written through; the parent
      // re-reads the part either way (mirror text, stored rollup).
      if (onChanged) onChanged();
    } catch (e) {
      setError(e.message || "Apply failed.");
    } finally {
      setBusyId(null);
    }
  };

  if (!partId) return null;

  const pricesFor = (step) => prices.filter((p) => p.process_id === step.process_id);

  return (
    <div className="card mb-3">
      <div className="card-header d-flex justify-content-between align-items-center">
        <strong>Outsourcing</strong>
        <button type="button" className="btn btn-sm btn-outline-secondary"
                onClick={load} disabled={loading}>
          {loading ? "Loading…" : "Refresh"}
        </button>
      </div>
      <div className="card-body">
        {error && <div className="alert alert-danger py-2 mb-2">{error}</div>}
        {notice && (
          <div className="alert alert-success py-2 mb-2 d-flex justify-content-between align-items-start">
            <div className="small">{notice}</div>
            <button type="button" className="btn-close btn-sm ms-2" aria-label="Dismiss" onClick={() => setNotice("")}></button>
          </div>
        )}
        {!loading && steps.length === 0 && (
          <div className="text-muted small">
            This part has no routing steps yet. Save its Processes first.
          </div>
        )}
        {steps.length > 0 && (
          <table className="table table-sm align-middle mb-0">
            <thead>
              <tr>
                <th style={{ width: "3rem" }}>#</th>
                <th>Step</th>
                <th style={{ width: "9rem" }}>Outsourced</th>
                <th style={{ width: "16rem" }}>Vendor</th>
                <th style={{ width: "10rem" }}>$ / piece</th>
                <th>Prices on file</th>
              </tr>
            </thead>
            <tbody>
              {steps.map((s) => {
                const busy = busyId === s.part_process_id;
                const others = pricesFor(s);
                const book = preferredPriceFor(s, prices);
                const rowIsBook = stepMatchesBook(s, book);
                const updated = s.updated_at ? asOf(s.updated_at).replace(/^as of /, 'updated ') : '';
                return (
                  <tr key={s.part_process_id}>
                    <td className="text-muted">{s.sequence}</td>
                    <td>
                      <div>{s.process_name}</div>
                      {s.sub_process && <div className="small text-muted">{s.workstation}</div>}
                    </td>
                    <td>
                      <div className="form-check" title={s.outsource_blocked_reason || ""}>
                        <input
                          className="form-check-input"
                          type="checkbox"
                          checked={!!s.is_outsourced}
                          /* Never disable UN-checking: you can always bring work back in house. */
                          disabled={busy || (!s.can_outsource && !s.is_outsourced)}
                          onChange={(e) => patchStep(s, { is_outsourced: e.target.checked })}
                        />
                        {!s.can_outsource && !s.is_outsourced && (
                          <span className="ms-1 text-muted" style={{ cursor: "help" }}>ⓘ</span>
                        )}
                      </div>
                    </td>
                    <td>
                      <select
                        className="form-select form-select-sm"
                        value={s.vendor_id || ""}
                        disabled={busy || !s.is_outsourced}
                        onChange={(e) =>
                          patchStep(s, { vendor_id: e.target.value ? parseInt(e.target.value, 10) : null })
                        }
                      >
                        <option value="">— none —</option>
                        {vendors.map((v) => (
                          <option key={v.vendor_id} value={v.vendor_id}>{v.name}</option>
                        ))}
                      </select>
                    </td>
                    <td>
                      {/* Uncontrolled on purpose (typing must not PATCH per keystroke), so the
                          key carries the server value: a write-through (Apply, RFQ award, another
                          session) re-mounts the input with the fresh figure instead of leaving
                          the stale defaultValue on screen. */}
                      <input
                        key={`${s.part_process_id}:${s.outsourced_cost_per_unit ?? ""}`}
                        type="number" step="0.0001" min="0"
                        className="form-control form-control-sm"
                        aria-label={`${s.process_name} $ per piece`}
                        defaultValue={s.outsourced_cost_per_unit ?? ""}
                        disabled={busy || !s.is_outsourced}
                        onBlur={(e) => {
                          const raw = e.target.value;
                          const next = raw === "" ? null : parseFloat(raw);
                          if (next === (s.outsourced_cost_per_unit ?? null)) return;
                          patchStep(s, { outsourced_cost_per_unit: next });
                        }}
                      />
                      <div className="small text-muted" style={{ lineHeight: 1.3 }}>
                        {rowIsBook ? <span className="badge bg-success" title="This row carries the preferred vendor's book price">book · {book.vendor_name}</span> : null}
                        {updated ? <span className={rowIsBook ? "ms-1" : ""}>{updated}</span> : null}
                      </div>
                    </td>
                    <td className="small">
                      {others.length === 0 ? (
                        <span className="text-muted">—</span>
                      ) : (
                        others.map((p) => {
                          const isApplied = p.is_preferred && stepMatchesBook(s, p);
                          return (
                            <div key={p.vendor_process_price_id} className="d-flex align-items-center flex-wrap gap-1">
                              {p.is_preferred && <span className="badge bg-success">preferred</span>}
                              <span>{p.vendor_name}: {fmtMoney(p.price_per_piece, { precise: true, dash: true })}</span>
                              <span className="text-muted">({p.source}
                                {p.price_age_days != null ? `, ${p.price_age_days}d old` : ""})</span>
                              {isApplied ? (
                                <span className="badge bg-light text-dark border" title="This row's $/piece is this book price">applied to row</span>
                              ) : (
                                <button
                                  type="button"
                                  className="btn btn-link btn-sm p-0"
                                  disabled={busy || !s.is_outsourced}
                                  title={s.is_outsourced
                                    ? (p.is_preferred
                                      ? "Write this book price to the part's row (and parts.coating_cost_per_unit for Coating)"
                                      : "Make this the preferred vendor and write its $/piece to the part's row (and parts.coating_cost_per_unit for Coating)")
                                    : "Flag the step outsourced first — the price book only writes through to an outsourced step"}
                                  onClick={() => applyBookPrice(s, p)}
                                >
                                  {p.is_preferred ? "Apply" : "Prefer & apply"}
                                </button>
                              )}
                              {p.is_preferred && !isApplied && s.is_outsourced ? (
                                <span className="badge bg-warning text-dark" title="The preferred book price is not what this part's row charges — Apply writes it through">≠ $/piece on this part</span>
                              ) : null}
                            </div>
                          );
                        })
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        {steps.length > 0 && (
          <div className="small text-muted mt-2">
            Outsourced $/piece is edited here (the Part page's "Coating $/unit" box is a read-only mirror of the
            Coating row). <strong>Apply</strong> writes the preferred vendor's book price to the row — and to the
            part's coating column for Coating — and recomputes the stored cost.
          </div>
        )}
        {steps.some((s) => !s.can_outsource && s.outsource_blocked_reason) && (
          <div className="small text-muted mt-2">
            {steps.filter((s) => !s.can_outsource && s.outsource_blocked_reason).map((s) => (
              <div key={s.part_process_id}>
                <strong>{s.process_name}:</strong> {s.outsource_blocked_reason}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
